import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../assests/CSS/search.css';

const servicesList = [
  "EOR/PEO/AOR",
  "Incorporation/Entity Setup",
  "Staffing/Talent Acquisition",
  "Global Payroll",
  "HRIS (Human Resources Information System)",
  "Accounting Services",
  "Tax Services",
  "Compliance Services",
  "M&A Services",
  "Software Solutions",
  "MSP (Managed Service Provider)"
];

const countriesList = [
  "United States",
  "India",
  "Australia",
  "Singapore",
  "Malaysia",
  "Qatar",
  "UAE",
  "France",
  "Canada",
  "Brazil",
  "Argentina",
  "Chile"
];

const Search = ({ onSearch }) => {
  const [formData, setFormData] = useState({ search: '', location: '' });
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [animatedPlaceholder, setAnimatedPlaceholder] = useState(servicesList[0]);
  const [fade, setFade] = useState(true);

  const [filteredServiceSuggestions, setFilteredServiceSuggestions] = useState([]);
  const [filteredCountrySuggestions, setFilteredCountrySuggestions] = useState([]);

  const [showServiceSuggestions, setShowServiceSuggestions] = useState(false);
  const [showCountrySuggestions, setShowCountrySuggestions] = useState(false);

  const [selectedServiceIndex, setSelectedServiceIndex] = useState(-1);
  const [selectedCountryIndex, setSelectedCountryIndex] = useState(-1);

  const navigate = useNavigate();

  // Animate placeholder text
  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        const nextIndex = (placeholderIndex + 1) % servicesList.length;
        setPlaceholderIndex(nextIndex);
        setAnimatedPlaceholder(servicesList[nextIndex]);
        setFade(true);
      }, 200);
    }, 2000);
    return () => clearInterval(interval);
  }, [placeholderIndex]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    if (name === 'search') {
      const matches = value
        ? servicesList.filter(item =>
            item.toLowerCase().includes(value.toLowerCase())
          )
        : [];
      setFilteredServiceSuggestions(matches);
      setShowServiceSuggestions(matches.length > 0);
      setSelectedServiceIndex(-1);
    }

    if (name === 'location') {
      const matches = value
        ? countriesList.filter(item =>
            item.toLowerCase().includes(value.toLowerCase())
          )
        : [];
      setFilteredCountrySuggestions(matches);
      setShowCountrySuggestions(matches.length > 0);
      setSelectedCountryIndex(-1);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (type, suggestion) => {
    setFormData((prev) => ({
      ...prev,
      [type]: suggestion
    }));
    if (type === 'search') {
      setShowServiceSuggestions(false);
    } else {
      setShowCountrySuggestions(false);
    }
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.search.trim() || !formData.location.trim()) {
      return;
    }

    if (onSearch) {
      onSearch(formData);
    }
    navigate('/insights', { state: { searchData: formData } });
    setShowServiceSuggestions(false);
    setShowCountrySuggestions(false);
  };

  // Highlight matched text
  const highlightMatch = (text, query) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <span key={index} className="highlight">{part}</span>
      ) : (
        <span key={index}>{part}</span>
      )
    );
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, type) => {
    const isService = type === 'search';
    const suggestions = isService ? filteredServiceSuggestions : filteredCountrySuggestions;
    const selectedIndex = isService ? selectedServiceIndex : selectedCountryIndex;
    const setSelectedIndex = isService ? setSelectedServiceIndex : setSelectedCountryIndex;
    const setShowSuggestions = isService ? setShowServiceSuggestions : setShowCountrySuggestions;
    const setValue = (val) =>
      setFormData((prev) => ({ ...prev, [type]: val }));

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!suggestions.length) return;
      const nextIndex = (selectedIndex + 1) % suggestions.length;
      setSelectedIndex(nextIndex);
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!suggestions.length) return;
      const nextIndex = (selectedIndex - 1 + suggestions.length) % suggestions.length;
      setSelectedIndex(nextIndex);
    }

    if (e.key === 'Enter') {
      if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
        e.preventDefault();
        const selected = suggestions[selectedIndex];
        setValue(selected);
        setSelectedIndex(-1);
        setShowSuggestions(false);
      }
    }
  };

  return (
    <>
      <form className="hero-form" onSubmit={handleSubmit}>
        <div className="input-group">
          <div className="input-wrapper autocomplete-wrapper">
            <i className="fa-solid fa-magnifying-glass"></i>
            <input
              type="text"
              name="search"
              className={`placeholder-fade ${fade ? 'fade-in' : 'fade-out'}`}
              placeholder={animatedPlaceholder}
              value={formData.search}
              onChange={handleChange}
              onKeyDown={(e) => handleKeyDown(e, 'search')}
              autoComplete="off"
            />
            {showServiceSuggestions && (
              <ul className="suggestions-list">
                {filteredServiceSuggestions.map((item, index) => (
                  <li
                    key={index}
                    onClick={() => handleSuggestionClick('search', item)}
                    className={index === selectedServiceIndex ? 'selected' : ''}
                  >
                    {highlightMatch(item, formData.search)}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="input-wrapper autocomplete-wrapper">
            <i className="fa-solid fa-location-dot"></i>
            <input
              type="text"
              name="location"
              placeholder="Location"
              value={formData.location}
              onChange={handleChange}
              onKeyDown={(e) => handleKeyDown(e, 'location')}
              autoComplete="off"
            />
            {showCountrySuggestions && (
              <ul className="suggestions-list">
                {filteredCountrySuggestions.map((item, index) => (
                  <li
                    key={index}
                    onClick={() => handleSuggestionClick('location', item)}
                    className={index === selectedCountryIndex ? 'selected' : ''}
                  >
                    {highlightMatch(item, formData.location)}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <button type="submit" className="get-started-btn">
            Get Started
          </button>
        </div>
      </form>
    </>
  );
};

export default Search;
